import styled from '@emotion/styled';

import { mixpanelObject } from 'src/utils/analytics';
import { OtherIconsSet } from 'src/utils/constants/IconsConstants';
import { getCurrencySymbol } from 'src/utils/SubscriptionHelper/SubscriptionHelper';

import { Data } from '../../services/responseTypes/displayPlansResponse';
import { colors } from '../../styles/colors';
import { ZeeFontRegular, ZeeFontBold, getOtherIcons } from '../../styles/font';
import { flex } from '../../styles/utils';
import { subscriptionPlanViewedEvent } from '../../utils/analytics/mixpanel/mixpanelEventsHelper';
import { DynamicObject } from '../../utils/commonTypes';
import { HamburgerMenuLinks } from '../../utils/constants/RouterConstants';
import {
  MIXPANEL_POP_UP_NAME,
  MIXPANEL_CONSUMPTION_BUY_PLAN,
  MIXPANEL_POP_UP_GROUP,
  MIXPANEL_CONSUMPTION_SUBSCRIPTION,
  MIXPANEL_PAGE_NAME,
  MIXPANEL_SUBSCRIPTION_PLAN_PAGE
} from '../../utils/analytics/mixpanel/mixpanelConstants';

const SpecialSubscriptionPlanCard = styled('div')<{ isDiscount: boolean }>`
  width: 100%;
  background: ${colors.AppBgColor};
  border: 1px solid
    ${(props) =>
      props.isDiscount
        ? colors.AccentPromoCodeSuccessColor
        : colors.PurpleLight};
  border-radius: 10px;
  max-width: 420px;
  margin: 0 auto;
`;

const PremiumName = styled('div')`
  ${ZeeFontRegular(16)}
  color: ${colors.white};
`;

const PremiumDetails = styled('span')<{ linethrough: boolean }>`
  ${ZeeFontRegular(12)}
  margin-top: ${(props) => (props?.linethrough ? '0px' : '8px')};
  text-align: start;
  color: ${colors.greyText};
  text-decoration: ${(props) => (props?.linethrough ? 'line-through' : 'none')};
`;

const PremiumDetailsContainer = styled.div<{ premiumDescription: boolean }>`
  flex-basis: ${(props) => (props?.premiumDescription ? '65%' : '35%')};
  align-items: ${(props) =>
    props?.premiumDescription ? 'center' : 'flex-start'};
  ${flex('column', 'center', 'flex-start')}
  width: 100%;
`;

const PremiumDetailsOuterContainer = styled('div')`
  ${flex()}
  padding: 25px 20px;
  height: inherit;
`;

const PremiumCardDesign = styled('div')`
  background: url(/webapp-assets/images/4kSubscriptionCardBgLayer1.svg) top
    no-repeat;
  border-radius: 8px;
  height: 100%;
  overflow: hidden;
  width: 100%;
`;

const PremiumDesignLayer = styled('div')`
  background: url(/webapp-assets/images/4kSubscriptionCardBgLayer2.svg) top
    no-repeat;
  height: 100%;
`;

const PremiumPrice = styled('span')`
  ${ZeeFontBold(16)}
  margin-top: 0;
`;

const StrongText = styled('strong')`
  ${ZeeFontBold(12)};
  color: ${colors.white};
`;

const SuggestedTextContainer = styled('div')<{ isDiscount: boolean }>`
  background: ${(props) =>
    props.isDiscount
      ? colors.AccentPromoCodeSuccessColor
      : `linear-gradient(270deg, #5730C7 0%, #5C2ADE 48.11%, #2928D4 94.83%)`};
  box-shadow: 0px 4px 4px rgb(0 0 0 / 25%);
  border-radius: 3px;
  position: absolute;
  padding: 0 20px;
  top: ${(props) => (props.isDiscount ? `27%` : `12%`)};
  left: 10%;
`;

const SuggestedText = styled('p')`
  ${ZeeFontRegular(10, 20)}
  color: ${colors.white};
`;

const TickContainer = styled('div')<{ isDiscount: boolean }>`
  background-color: ${colors.white};
  border-radius: 50%;
  color: ${colors.Purple};
  padding: 3px 5px;
  position: absolute;
  right: ${(props) => (props.isDiscount ? `4%` : `4%`)};
  top: ${(props) => (props.isDiscount ? `27%` : `10%`)};
`;
const TickIcon = styled('span')`
  ${getOtherIcons(OtherIconsSet.TICK)}
`;

const CardFooter = styled('div')`
  padding: 10px;
  text-align: center;
  border-top: 1px dashed ${colors.Gray4F};
`;
const FooterLink = styled('a')`
  ${ZeeFontRegular(14, 22)};
  color: ${colors.white};
  cursor: pointer;
`;

const subscriptionClickHandler = (e: React.MouseEvent<HTMLElement>) => {
  e.preventDefault();
  window.location.href = HamburgerMenuLinks.BUY_PLAN;
};

type AppProps = {
  plansData: Data;
  newSubscriptionStrings: DynamicObject;
  planExpiryDate?: Date | null;
  showDescriptionAndScreenCount?: boolean;
  renderSuggestedText?: boolean;
  renderTick?: boolean;
  renderCardFooter?: boolean;
  isDiscount: boolean;
};

const HighestPlanPage = ({
  plansData,
  planExpiryDate,
  newSubscriptionStrings,
  showDescriptionAndScreenCount,
  renderSuggestedText,
  renderTick,
  renderCardFooter,
  isDiscount
}: AppProps) => {
  const {
    title: planTitle,
    display_price,
    actual_price,
    currency,
    features: { SCREENS = {} } = {}
  } = plansData?.plans?.[0] || {};

  let planActiveTill = '';

  if (planExpiryDate) {
    let dateString: string | string[] = new Date(planExpiryDate).toUTCString();
    dateString = dateString?.split(' ');
    planActiveTill = `${dateString?.[1]}
    ${dateString?.[2]}, ${dateString?.[3]}`;
  }

  // mixpanel event - Revised Subscription Journey
  const triggerRSJMixpanelEvents = () => {
    mixpanelObject.setProperty(
      MIXPANEL_POP_UP_NAME,
      MIXPANEL_CONSUMPTION_BUY_PLAN
    );
    mixpanelObject.setProperty(
      MIXPANEL_POP_UP_GROUP,
      MIXPANEL_CONSUMPTION_SUBSCRIPTION
    );
    mixpanelObject.setProperty(
      MIXPANEL_PAGE_NAME,
      MIXPANEL_SUBSCRIPTION_PLAN_PAGE
    );
    subscriptionPlanViewedEvent();
  };

  // card footer section click handler
  const handleFooterClick = () => {
    triggerRSJMixpanelEvents();
  };

  const renderDescription = () => {
    let component;
    if (planExpiryDate) {
      let dateString: string | string[] = new Date(
        planExpiryDate
      ).toUTCString();
      dateString = dateString?.split(' ');
      planActiveTill = `${dateString?.[1]}
      ${dateString?.[2]}, ${dateString?.[3]}`;
      const text = `${newSubscriptionStrings.ACTIVE_TILL} ${planActiveTill}`;
      component = <PremiumDetails linethrough={false}>{text}</PremiumDetails>;
    } else if (showDescriptionAndScreenCount) {
      let watchNowText = newSubscriptionStrings.CMS_BOTTOMSHEET_SCREENS_TEXT;
      watchNowText = watchNowText.split('{{number_of_screens}}');
      component = (
        <>
          <PremiumDetails linethrough={false}>
            {newSubscriptionStrings.CMS_BOTTOMSHEET_HDPLANDESC_TEXT}
          </PremiumDetails>
          <PremiumDetails linethrough={false}>
            {`${watchNowText[0]} `}
            <StrongText>
              {SCREENS?.title} {newSubscriptionStrings.screens}
            </StrongText>
            {` ${watchNowText[1]}`}
          </PremiumDetails>
        </>
      );
    }
    return component;
  };

  const renderSuggestionText = () => {
    const {
      PlanSelection_PlanCard_Discounted_Tag_Text,
      PlanCard_Banner_Suggested_For_You_Text
    } = newSubscriptionStrings;
    return isDiscount
      ? PlanSelection_PlanCard_Discounted_Tag_Text
      : PlanCard_Banner_Suggested_For_You_Text;
  };

  return (
    <SpecialSubscriptionPlanCard
      isDiscount={isDiscount}
      data-test-id="specialSubscriptionPlanCard">
      {renderSuggestedText && (
        <SuggestedTextContainer isDiscount={isDiscount}>
          <SuggestedText>{renderSuggestionText()}</SuggestedText>
        </SuggestedTextContainer>
      )}
      {renderTick && (
        <TickContainer isDiscount={isDiscount}>
          <TickIcon />
        </TickContainer>
      )}
      <PremiumCardDesign>
        <PremiumDesignLayer>
          <PremiumDetailsOuterContainer data-test-id="premiumDetailsContainer">
            <PremiumDetailsContainer premiumDescription={true}>
              <PremiumName data-test-id="premiumName">{planTitle}</PremiumName>
              {renderDescription()}
            </PremiumDetailsContainer>
            <PremiumDetailsContainer
              data-test-id="premiumDetailsContainer"
              premiumDescription={false}>
              {display_price && (
                <PremiumDetails
                  data-test-id="premiumDetails"
                  linethrough={true}>
                  {getCurrencySymbol(currency)}
                  {display_price}
                </PremiumDetails>
              )}
              <PremiumDetails data-test-id="premiumDetails" linethrough={false}>
                <PremiumPrice data-test-id="premiumPrice">
                  {getCurrencySymbol(currency)}
                  {actual_price}
                </PremiumPrice>
                /{newSubscriptionStrings.YEAR}
              </PremiumDetails>
            </PremiumDetailsContainer>
          </PremiumDetailsOuterContainer>
          {renderCardFooter && (
            <CardFooter onClick={handleFooterClick}>
              <FooterLink
                onClick={subscriptionClickHandler}
                href={HamburgerMenuLinks.BUY_PLAN}
                data-testid="explore-all-plans-btn">
                {newSubscriptionStrings.CMS_BOTTOMSHEET_EXPLOREPLANS_TEXT}
              </FooterLink>
            </CardFooter>
          )}
        </PremiumDesignLayer>
      </PremiumCardDesign>
    </SpecialSubscriptionPlanCard>
  );
};
export default HighestPlanPage;
